import lozad from 'lozad'
import { trapFocusHandlers } from '@/scripts'

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
	summary.setAttribute('role', 'button')
	summary.setAttribute(
		'aria-expanded',
		summary.parentNode.hasAttribute('open')
	)

	if (summary.nextElementSibling.getAttribute('id')) {
		summary.setAttribute('aria-controls', summary.nextElementSibling.id)
	}

	summary.addEventListener('click', (event) => {
		event.currentTarget.setAttribute(
			'aria-expanded',
			!event.currentTarget.closest('details').hasAttribute('open')
		)
	})

	summary.parentElement.addEventListener('keyup', onKeyUpEscape)
})

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
	document.querySelector(':focus-visible')
} catch (e) {
	focusVisiblePolyfill()
}

function focusVisiblePolyfill() {
	const navKeys = [
		'ARROWUP',
		'ARROWDOWN',
		'ARROWLEFT',
		'ARROWRIGHT',
		'TAB',
		'ENTER',
		'SPACE',
		'ESCAPE',
		'HOME',
		'END',
		'PAGEUP',
		'PAGEDOWN',
	]
	let currentFocusedElement = null
	let mouseClick = null

	window.addEventListener('keydown', (event) => {
		if (navKeys.includes(event.code.toUpperCase())) {
			mouseClick = false
		}
	})

	window.addEventListener('mousedown', (event) => {
		mouseClick = true
	})

	window.addEventListener(
		'focus',
		() => {
			if (currentFocusedElement)
				currentFocusedElement.classList.remove('focused')

			if (mouseClick) return

			currentFocusedElement = document.activeElement
			currentFocusedElement.classList.add('focused')
		},
		true
	)
}

export function onKeyUpEscape(event) {
	if (event.code.toUpperCase() !== 'ESCAPE') return

	const openDetailsElement = event.target.closest('details[open]')
	if (!openDetailsElement) return

	const summaryElement = openDetailsElement.querySelector('summary')
	openDetailsElement.removeAttribute('open')
	summaryElement.setAttribute('aria-expanded', false)
	summaryElement.focus()
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
	window.Shopify = {}
}

Shopify.bind = function (fn, scope) {
	return function () {
		return fn.apply(scope, arguments)
	}
}

Shopify.setSelectorByValue = function (selector, value) {
	for (var i = 0, count = selector.options.length; i < count; i++) {
		var option = selector.options[i]
		if (value == option.value || value == option.innerHTML) {
			selector.selectedIndex = i
			return i
		}
	}
}

Shopify.addListener = function (target, eventName, callback) {
	target.addEventListener
		? target.addEventListener(eventName, callback, false)
		: target.attachEvent('on' + eventName, callback)
}

Shopify.postLink = function (path, options) {
	options = options || {}
	var method = options['method'] || 'post'
	var params = options['parameters'] || {}

	var form = document.createElement('form')
	form.setAttribute('method', method)
	form.setAttribute('action', path)

	for (var key in params) {
		var hiddenField = document.createElement('input')
		hiddenField.setAttribute('type', 'hidden')
		hiddenField.setAttribute('name', key)
		hiddenField.setAttribute('value', params[key])
		form.appendChild(hiddenField)
	}
	document.body.appendChild(form)
	form.submit()
	document.body.removeChild(form)
}

type HTMLElementProperty =
	| 'parentNode'
	| 'firstChild'
	| 'lastChild'
	| 'previousSibling'
	| 'nextSibling'
	| 'firstElementChild'
	| 'lastElementChild'
	| 'previousElementSibling'
	| 'nextElementSibling'
	| 'textContent'
	| 'parentElement'

type CommonEventType = MouseEvent | KeyboardEvent | TouchEvent

// begin typescript DOM functions
// this is a set of functions I wrote while refactoring Dawn 9.0 to typescript
// ended up being very flexible and useful for enforcing types when accessing dom elements so we are reusing for Dawn 10.0

export const qsRequired = <
	T extends HTMLElement = HTMLElement,
	U extends HTMLElement = T
>(
	selector: string,
	component?: HTMLElement,
	additionalProperty?: HTMLElementProperty
) => {
	const container = component ?? document
	const element = container.querySelector(selector) as T | null
	if (!element) throw new Error(`required element not found: ${selector}`)
	if (!additionalProperty) return element
	const elFromProperty = element[additionalProperty] as U | null
	if (!elFromProperty)
		throw new Error(`required element not found: ${selector}`)
	return elFromProperty
}

export const qsRequiredFromDocument = <T extends HTMLElement = HTMLElement>(
	selector: string,
	parsedDocument: Document
) => {
	const element = parsedDocument.querySelector(selector) as T | null
	if (!element) throw `required element not found: ${selector}`
	return element
}

// required querySelectorAll
export const qsaRequired = <T extends HTMLElement = HTMLElement>(
	selector: string,
	component?: HTMLElement | Document
) => {
	const container = component ?? document
	const elements = container.querySelectorAll(selector) as NodeListOf<T>
	if (!elements.length)
		throw new Error(`required element group not found: ${selector}`)
	return elements
}

// return undefined if the HTML element isn't found
export const qsOptional = <T extends HTMLElement = HTMLElement>(
	selector: string,
	component?: HTMLElement | Document
) => {
	const container = component ?? document
	const element = container.querySelector(selector) as T | null
	return element ?? undefined
}

// optional querySelectorAll typecast
export const qsaOptional = <T extends HTMLElement = HTMLElement>(
	selector: string,
	component?: HTMLElement | Document
) => {
	const container = component ?? document
	const elements = container.querySelectorAll(selector) as NodeListOf<T>
	if (!elements.length) return undefined
	return elements
}

// required event targets
export const currentTargetRequired = <
	E extends Event = CommonEventType,
	T extends HTMLElement = HTMLElement
>(
	event: E,
	additionalProperty?: HTMLElementProperty
) => {
	const element = event.currentTarget as (EventTarget & T) | null
	if (!element)
		throw new Error(`required element not found: event.currentTarget`)
	if (!additionalProperty) return element
	const elFromProperty = element[additionalProperty] as T | null
	if (!elFromProperty)
		throw new Error(`required element not found: ${additionalProperty}`)
	return elFromProperty
}

export const targetRequired = <
	E extends Event = CommonEventType,
	T extends HTMLElement | Document = HTMLElement
>(
	event: E
) => {
	const element = event.target as T | null
	if (!element)
		throw new Error(`required element not found: event.currentTarget`)
	return element
}

// optional event targets. this is sort of not important but will probably make type casting easier
export const currentTargetOptional = <
	E extends Event = CommonEventType,
	T extends HTMLElement = HTMLElement
>(
	event: E
) => {
	return (event.currentTarget as EventTarget & T) ?? undefined
}

export const targetOptional = <
	E extends Event = CommonEventType,
	T extends HTMLElement = HTMLElement
>(
	event: E
) => {
	return (event.target as EventTarget & T) ?? undefined
}

export const targetClosestRequired = <
	E extends Event = CommonEventType,
	T extends HTMLElement = HTMLElement
>(
	event: E,
	selector: string
) => {
	const element = event.target as T | null
	if (!element)
		throw new Error(`required element not found: event.currentTarget`)
	const closest = element.closest(selector) as T
	if (!closest)
		throw new Error(
			`required element not found: ${element.nodeName}.closest(${selector})`
		)
	return closest
}

export const targetClosestOptional = <
	E extends Event = CommonEventType,
	T extends HTMLElement = HTMLElement
>(
	event: E,
	selector: string
) => {
	const element = event.target as T | null
	if (!element) return undefined
	const closest = element.closest(selector) as T
	if (!closest) return undefined
	return closest
}

// properties and methods of HTML elements that return an HTMLElement
export const closestRequired = <T extends HTMLElement = HTMLElement>(
	element: HTMLElement,
	selector: string
) => {
	const closest = element.closest(selector) as T
	if (!closest)
		throw new Error(
			`required element not found: ${element.nodeName}.closest(${selector})`
		)
	return closest
}

export const closestOptional = <T extends HTMLElement = HTMLElement>(
	element: HTMLElement,
	selector: string
) => {
	return (element.closest(selector) as T) ?? undefined
}

export const elementPropertyRequired = <T extends HTMLElement = HTMLElement>(
	element: HTMLElement,
	property: HTMLElementProperty
) => {
	const parent = element[property] as T | null
	if (!parent)
		throw new Error(
			`required element not found: ${element.nodeName}.parentNode`
		)
	return parent
}

// this function is use to safely define a new custom element
// it allows callbacks to be run before and after the element is defined -
// callback functionality is mainly for shop filters but can apply to any class with heavy usage of static methods

interface CustomElementConstructorWithStaticTagName
	extends CustomElementConstructor {
	htmlSelector: string
}

// throw error if something goes wrong with a constructor
export const safeDefineElement = (
	elementClass: CustomElementConstructorWithStaticTagName,
	callbackBefore?: Function,
	callbackAfter?: Function
): void => {
	try {
		if (customElements.get(elementClass.htmlSelector)) return
		if (callbackBefore instanceof Function) {
			const elsInDocument = qsaOptional(elementClass.htmlSelector)
			if (elsInDocument) {
				callbackBefore()
			}
		}
		customElements.define(elementClass.htmlSelector, elementClass)
		if (callbackAfter instanceof Function) {
			const elsInDocument = qsaOptional(elementClass.htmlSelector)
			if (elsInDocument) {
				callbackAfter()
			}
		}
	} catch (error) {
		console.error(
			`Failed to define custom element '${elementClass.htmlSelector}': ${error.message}`
		)
	}
}

// formData helpers
export const getOrThrow = <T extends FormData | Map<any, any> = FormData>(
	data: T,
	key: string
) => {
	const value = data.get(key)
	if (!value) throw new Error(`Value ${key} not found in ${data}`)
	return value
}
export const getOrUndefined = <T extends FormData | Map<any, any> = FormData>(
	data: T,
	key: string
) => {
	const value = data.get(key)
	if (!value) return undefined
	return value
}

// get attribute from element or throw error
export const getAttributeOrThrow = (attribute: string, el: HTMLElement) => {
	const data = el.getAttribute(attribute)
	if (!data)
		throw new Error(`Attribute ${attribute} no found on element ${el}`)
	return data
}
export const getAttributeOrUndefined = (attribute: string, el: HTMLElement) => {
	const data = el.getAttribute(attribute)
	return data ?? undefined
}
// browser safe replace all
export const replaceAll = (str: string, find: string, replace: string) => {
	return str.split(find).join(replace)
}

// copying helpers in
export type DebounceCallback = (...args: unknown[]) => unknown
export const debounce = (
	fn: Function,
	wait: number,
	device = 'all',
	immediate = false
): DebounceCallback => {
	if (device === 'touch' && !hasTouchScreen()) return (..._) => {}
	if (device === 'mouse' && hasTouchScreen()) return (..._) => {}
	let t: number | undefined
	if (immediate || t === undefined) {
		return (...args) => {
			if (!t) {
				fn.apply(this, args)
			}
			clearTimeout(t)
			t = setTimeout(() => {
				t = undefined
			}, wait)
		}
	}
	return (...args) => {
		clearTimeout(t)
		t = setTimeout(() => fn.apply(this, args), wait)
	}
}

export const fetchConfig = (type = 'json') => {
	return {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			Accept: `application/${type}`,
		},
	}
}

export const getBackendRoute = () =>
	'https://experiment-shopify-app.herokuapp.com'
//export const getBackendRoute = () => 'https://1c8d6502dd8f.ngrok.app';

export const getViewport = () => {
	const viewWidth = Math.max(
		document.documentElement.clientWidth,
		window.innerWidth || 0
	)
	if (viewWidth >= 990) {
		return 'm-up'
	} else {
		return 'm-down'
	}
}

export const hasTouchScreen = () => {
	return (
		'ontouchstart' in window ||
		navigator.maxTouchPoints > 0 ||
		navigator.msMaxTouchPoints > 0
	)
}

export const isElementVisible = (el: HTMLElement) => {
	return !!el?.offsetParent
}

export const pauseAllMedia = () => {
	document.querySelectorAll('.js-youtube').forEach((video) => {
		video.contentWindow.postMessage(
			'{"event":"command","func":"' + 'pauseVideo' + '","args":""}',
			'*'
		)
	})
	document.querySelectorAll('.js-vimeo').forEach((video) => {
		video.contentWindow.postMessage('{"method":"pause"}', '*')
	})
	document.querySelectorAll('video').forEach((video) => video.pause())
	document.querySelectorAll('product-model').forEach((model) => {
		if (model.modelViewerUI) model.modelViewerUI.pause()
	})
}

export const pauseAllPdpMedia = () => {
	document.querySelectorAll('.js-youtube').forEach((video) => {
		video.contentWindow.postMessage(
			'{"event":"command","func":"' + 'pauseVideo' + '","args":""}',
			'*'
		)
	})
	document.querySelectorAll('.js-vimeo').forEach((video) => {
		video.contentWindow.postMessage('{"method":"pause"}', '*')
	})
	document
		.querySelectorAll('video.allow-pause')
		.forEach((video) => video.pause())
	document.querySelectorAll('product-model').forEach((model) => {
		if (model.modelViewerUI) model.modelViewerUI.pause()
	})
}

export const ifThemeEditor = (loader) => {
	if (Shopify.designMode) {
		console.log('we are in shopify design mode')
		document.addEventListener('shopify:section:load', (e) => {
			if (!e.target) return
			const video = e.target.querySelectorAll(
				'video.lazyload:not([data-loaded="true"])'
			)
			if (video && video.length) {
				for (let i = 0; i < video.length; i++) {
					video[i].setAttribute('autoplay', '')
				}
			}
			const unloaded = e.target.querySelectorAll(
				'.lazyload:not([data-loaded="true"])'
			)
			if (!unloaded || !unloaded.length) return
			for (let i = 0; i < unloaded.length; i++) {
				loader.triggerLoad(unloaded[i])
			}
		})
	}
}

export const loadAssets = () => {
	const observer = lozad('.lazyload', {
		rootMargin: '10px 0px', // syntax similar to that of CSS Margin
		threshold: 0.1, // ratio of element convergence
	}) // passing a `NodeList` (e.g. `document.querySelectorAll()`) is also valid
	observer.observe()
	// start loading image assets after a few seconds (let the page load first)

	window.setTimeout(() => {
		const unloaded = document.querySelectorAll(
			'img.lazyload:not([data-loaded="true"])'
		)
		for (let i = 0; i < unloaded.length; i++) {
			if (!isElementVisible(unloaded[i])) continue
			window.setTimeout(() => {
				observer.triggerLoad(unloaded[i])
			}, parseInt(i) * 100)
		}
	}, 2500)
	// wait another 5 seconds and start loading footer and quiz vids
	window.setTimeout(() => {
		const unloadedVideo = document.querySelectorAll(
			'video.lazyload:not([data-loaded="true"])'
		)
		for (let i = 0; i < unloadedVideo.length; i++) {
			if (!isElementVisible(unloadedVideo[i])) continue
			window.setTimeout(() => {
				observer.triggerLoad(unloadedVideo[i])
			}, parseInt(i) * 300)
		}
	}, 6000)
	// play videos when in view and pause when not in view
	videoObserver(observer)
	// immediately load images in theme editor
	ifThemeEditor(observer)
}

export const getFocusableElements = (
	container: HTMLElement
): HTMLElement[] | undefined => {
	const focusableElements = qsaOptional(
		"summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe",
		container
	)
	if (focusableElements) return Array.from(focusableElements)
	else return undefined
}

export const trapFocus = (
	container?: HTMLElement,
	elementToFocus = container
) => {
	if (!container) return
	let elements = getFocusableElements(container)
	if (!elements) return
	let first = elements[0]
	let last = elements[elements.length - 1]

	removeTrapFocus()

	trapFocusHandlers.focusin = (event) => {
		if (
			event.target !== container &&
			event.target !== last &&
			event.target !== first
		)
			return
		if (trapFocusHandlers.keydown) {
			document.addEventListener('keydown', trapFocusHandlers.keydown)
		}
	}

	trapFocusHandlers.focusout = function () {
		if (trapFocusHandlers.keydown) {
			document.removeEventListener('keydown', trapFocusHandlers.keydown)
		}
	}

	trapFocusHandlers.keydown = function (event) {
		if (event.code.toUpperCase() !== 'TAB') return // If not TAB key
		// On the last focusable element and tab forward, focus the first element.
		if (event.target === last && !event.shiftKey) {
			event.preventDefault()
			first.focus()
		}

		//  On the first focusable element and tab backward, focus the last element.
		if (
			(event.target === container || event.target === first) &&
			event.shiftKey
		) {
			event.preventDefault()
			last.focus()
		}
	}

	document.addEventListener('focusout', trapFocusHandlers.focusout)
	document.addEventListener('focusin', trapFocusHandlers.focusin)

	elementToFocus?.focus()
}

export async function getSectionHTMLForResource(
	url: string,
	sectionId: string
): Promise<string | undefined> {
	try {
		return await fetch(`${url}?sections=${sectionId}`)
			.then((response) => {
				return response.json()
			})
			.then((response) => {
				const html = response[sectionId]
				const parser = new DOMParser()
				const doc = parser.parseFromString(html, 'text/html')
				return qsRequiredFromDocument('.shopify-section', doc).innerHTML
			})
	} catch (e) {
		console.error('error in getSectionHTMLForResource')
		console.log(e)
	}
}

export const removeTrapFocus = (elementToFocus?: HTMLElement) => {
	if (trapFocusHandlers.focusin) {
		document.removeEventListener('focusin', trapFocusHandlers.focusin)
	}
	if (trapFocusHandlers.focusout) {
		document.removeEventListener('focusout', trapFocusHandlers.focusout)
	}

	if (trapFocusHandlers.keydown) {
		document.removeEventListener('keydown', trapFocusHandlers.keydown)
	}

	if (elementToFocus) elementToFocus.focus()
}

export const videoObserver = (loader) => {
	let options = {
		root: null,
		rootMargin: '0px',
		threshold: 0.001,
	}

	let callback = (
		entries: IntersectionObserverEntry[],
		observer: IntersectionObserver
	) => {
		entries.forEach((entry) => {
			if (entry.isIntersecting) {
				if (entry.target.getAttribute('data-loaded') !== 'true') {
					entry.target.setAttribute('autoplay', '')
					loader.triggerLoad(entry.target)
				}
				entry.target.play()
			} else {
				entry.target.pause()
			}
		})
	}

	let observer = new IntersectionObserver(callback, options)
	const videos = document.querySelectorAll('video:not([autoplay])')

	videos.forEach((video) => {
		observer.observe(video)
	})
}

export function formatPhoneNumber(unchecked_number: string) {
	let phone_number = unchecked_number
		.replace('(', '')
		.replace(')', '')
		.replace('+', '')
		.replaceAll('-', '')
		.replaceAll(' ','')
		.trim()
	/*if (window?.Shopify?.country === 'US' && phone_number.length === 10) {
		phone_number = `+1${phone_number}`
	}*/
	phone_number = `+1${phone_number}`
	return phone_number
}
